import { Vector2, Vector3, Color, Raycaster, WebGLRenderTarget, HalfFloatType } from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { FilmPass } from 'three/examples/jsm/postprocessing/FilmPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { GammaCorrectionShader } from 'three/examples/jsm/shaders/GammaCorrectionShader.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass.js';
import vignetteVertex from '../Shaders/vignetteVertex.glsl';
import vignetteFragment from '../Shaders/vignetteFragment.glsl';
import gradeFragment from '../Shaders/gradeFragment.glsl';
import Experience from '../Experience.js'

export default class Composer {
    // Caméra active (perspective ou plan orthographique)
    get camera() {
        return this.experience.camera.instance;
    }

    constructor() {
        this.experience = new Experience()
        this.renderer = this.experience.renderer.instance;
        this.sizes = this.experience.sizes;
        this.scene = this.experience.scene;
        this.debug = this.experience.debug;
        this.isMobile = this.experience.isMobile;
        this.config = this.experience.config;
        this.qualityConfig = this.experience.config.quality;
        this.time = this.experience.time;

        this.raycaster = new Raycaster();
        this.screenCenter = new Vector2(0, 0);

        this.qualityLevel = this.resolveInitialQualityLevel();
        this.fpsAccumulator = 0;
        this.fpsSamples = 0;
        this.fpsWindowMs = this.qualityConfig?.auto?.sampleWindowMs ?? 1800;
        this.autoDownshiftFps = this.qualityConfig?.auto?.downshiftFps ?? 52;
        this.autoUpshiftFps = this.qualityConfig?.auto?.upshiftFps ?? 58;
        this.qualityOrder = ['medium', 'high', 'ultra'];

        this.applyQualityProfile(this.qualityLevel);

        // Cible HDR multi-échantillonnée : le renderer tourne sans antialias
        // natif, le MSAA se fait ici (bords du relief, arbres, bâtiments).
        this.composer = new EffectComposer(this.renderer, this.createRenderTarget());
        this.applyMsaaSamples(this.getQualityProfile().msaaSamples ?? 0);
        this.composer.setPixelRatio(this.pixelRatio);
        this.composer.setSize(
            this.sizes.width / this.factor,
            this.sizes.height / this.factor
        );

        if (this.debug.active) {
            this.debugFolder = this.debug.ui.addFolder({ title: 'Post-Processing', expanded: false });
        }

        this.addRenderPass();
        if (this.config.postprocessing.bloomPass?.enabled) this.addBloomPass();
        if (this.config.postprocessing.filmPass?.enabled) this.addFilmPass();
        if (!this.isMobile && this.config.postprocessing.bokehPass?.enabled) this.addDOFPass();

        // La passe d'étalonnage intègre tone mapping, vignette et sortie sRGB :
        // elle remplace alors les passes vignette + gamma historiques.
        if (this.config.postprocessing.gradePass?.enabled) {
            this.addGradePass();
        } else {
            if (this.config.postprocessing.vignettePass?.enabled) this.addVignettePass();
            if (this.config.postprocessing.gammaCorrectionPass?.enabled) this.addGammaCorrectionPass();
        }
    }

    createRenderTarget() {
        const target = new WebGLRenderTarget(1, 1, { type: HalfFloatType });
        target.texture.name = 'EffectComposer.rt1';
        return target;
    }

    applyMsaaSamples(samples) {
        if (!this.composer) return;
        const targets = [this.composer.renderTarget1, this.composer.renderTarget2];
        targets.forEach(target => {
            if (target.samples === samples) return;
            target.samples = samples;
            target.dispose();
        });
    }

    addGradePass() {
        const grade = this.config.postprocessing.gradePass;
        const vignette = this.config.postprocessing.vignettePass ?? { offset: 0.3, darkness: 1 };

        this.gradePass = new ShaderPass({
            uniforms: {
                tDiffuse: { value: null },
                resolution: { value: new Vector2(this.sizes.width, this.sizes.height) },
                time: { value: 0 },
                exposure: { value: this.renderer.toneMappingExposure },
                contrast: { value: grade.contrast ?? 1 },
                saturation: { value: grade.saturation ?? 1 },
                shadowTint: { value: this.colorToVector(grade.shadowTint ?? '#808080') },
                highlightTint: { value: this.colorToVector(grade.highlightTint ?? '#808080') },
                splitToneStrength: { value: grade.splitToneStrength ?? 0 },
                offset: { value: vignette.enabled === false ? 10 : vignette.offset },
                darkness: { value: vignette.darkness },
                grain: { value: this.isMobile ? (grade.grainMobile ?? 0) : (grade.grain ?? 0) }
            },
            vertexShader: vignetteVertex,
            fragmentShader: gradeFragment
        });
        this.composer.addPass(this.gradePass);

        if (this.debug.active) {
            const u = this.gradePass.uniforms;
            const f = this.debugFolder.addFolder({ title: 'Grade', expanded: false });
            f.addBinding(u.contrast, 'value', { label: 'contrast', min: 0.7, max: 1.4, step: 0.01 });
            f.addBinding(u.saturation, 'value', { label: 'saturation', min: 0.5, max: 1.4, step: 0.01 });
            f.addBinding(u.splitToneStrength, 'value', { label: 'split tone', min: 0, max: 0.5, step: 0.01 });
            f.addBinding(u.offset, 'value', { label: 'vignette offset', min: 0, max: 1, step: 0.01 });
            f.addBinding(u.darkness, 'value', { label: 'vignette', min: 0, max: 3, step: 0.01 });
            f.addBinding(u.grain, 'value', { label: 'grain', min: 0, max: 0.08, step: 0.001 });
        }
    }

    // Les teintes du split toning sont exprimées en sRGB : 0.5 = neutre.
    colorToVector(hex) {
        const c = new Color();
        c.setStyle(hex, 'srgb-linear');
        return new Vector3(c.r, c.g, c.b);
    }

    addRenderPass() {
        this.renderPass = new RenderPass(this.scene, this.camera);
        this.composer.addPass(this.renderPass);
    }

    addBloomPass() {
        const config = this.config.postprocessing.bloomPass ?? { strength: 0.5, radius: 0.5, threshold: 0.5 };

        const bloomResolution = new Vector2(
            this.sizes.width / 4,
            this.sizes.height / 4
        );

        this.bloomPass = new UnrealBloomPass(
            bloomResolution,
            config.strength,
            config.radius,
            config.threshold
        );

        this.composer.addPass(this.bloomPass);

        if (this.debug.active) {
            const f = this.debugFolder.addFolder({ title: 'Bloom', expanded: false });
            this.bloomBindings = {
                strength: f.addBinding(this.bloomPass, 'strength', { min: 0, max: 2, step: 0.01 }),
                radius: f.addBinding(this.bloomPass, 'radius', { min: 0, max: 1, step: 0.01 }),
                threshold: f.addBinding(this.bloomPass, 'threshold', { min: 0, max: 1, step: 0.01 })
            };
        }
    }

    addFilmPass() {
        const config = this.config.postprocessing.filmPass ?? { intensity: { value: 0.5 } };

        this.filmPass = new FilmPass(config.intensity.value);
        this.composer.addPass(this.filmPass);

        if (this.debug.active) {
            const f = this.debugFolder.addFolder({ title: 'Film', expanded: false });
            this.filmBinding = f.addBinding(this.filmPass.uniforms.intensity, 'value', { min: 0, max: 5 });
        }
    }

    addVignettePass() {
        const config = this.config.postprocessing.vignettePass ?? { offset: 0.5, darkness: 0.5 };

        const vignetteShader = {
            uniforms: {
                tDiffuse: { value: null },
                offset: { value: config.offset },
                darkness: { value: config.darkness }
            },
            vertexShader: vignetteVertex,
            fragmentShader: vignetteFragment
        };

        this.vignettePass = new ShaderPass(vignetteShader);
        this.composer.addPass(this.vignettePass);

        if (this.debug.active) {
            const f = this.debugFolder.addFolder({ title: 'Vignette', expanded: false });
            this.vignetteBindings = {
                offset: f.addBinding(this.vignettePass.uniforms.offset, 'value', { min: 0, max: 1 }),
                darkness: f.addBinding(this.vignettePass.uniforms.darkness, 'value', { min: 0, max: 10 })
            };
        }
    }

    addGammaCorrectionPass() {
        this.gammaCorrectionPass = new ShaderPass(GammaCorrectionShader);
        this.composer.addPass(this.gammaCorrectionPass);
    }

    addDOFPass() {
        const config = this.config.postprocessing.bokehPass;
        const cinematicConfig = config.cinematic ?? {};

        this.dofPass = new BokehPass(this.scene, this.camera, {
            focus: config.focus,
            aperture: config.aperture,
            maxblur: config.maxblur,
            width: this.sizes.width,
            height: this.sizes.height
        });

        this.baseDOF = {
            focus: config.focus,
            aperture: config.aperture,
            maxblur: config.maxblur
        };
        this.cinematicDOF = {
            enabled: cinematicConfig.enabled ?? false,
            feather: cinematicConfig.feather ?? 0.02,
            segments: cinematicConfig.segments ?? []
        };

        // Avoid full-time blur; enabled only on cinematic segments by default.
        this.dofPass.enabled = !this.cinematicDOF.enabled;

        this.composer.addPass(this.dofPass);

        if (this.debug.active) {
            const f = this.debugFolder.addFolder({ title: 'DOF', expanded: false });
            this.dofBindings = {
                focus: f.addBinding(this.dofPass.materialBokeh.uniforms['focus'], 'value', { min: 0, max: 10, step: 0.01 }),
                aperture: f.addBinding(this.dofPass.materialBokeh.uniforms['aperture'], 'value', { min: 0.001, max: 0.01, step: 0.001 }),
                maxblur: f.addBinding(this.dofPass.materialBokeh.uniforms['maxblur'], 'value', { min: 0, max: 0.03, step: 0.001 })
            };
            if (this.cinematicDOF) {
                f.addBinding(this.cinematicDOF, 'enabled', { label: 'Cinematic' });
                f.addBinding(this.cinematicDOF, 'feather', { min: 0.001, max: 0.08, step: 0.001, label: 'Feather' });
            }
        }
    }

    resolveInitialQualityLevel() {
        const configuredPreset = this.qualityConfig?.preset ?? 'auto';
        if (configuredPreset !== 'auto') return configuredPreset;
        if (this.isMobile) return 'medium';
        return this.sizes.width > 1920 ? 'high' : 'ultra';
    }

    getQualityProfile(level = this.qualityLevel) {
        const fallback = {
            rendererPixelRatioMax: 1.25,
            composerPixelRatio: 0.9,
            composerScaleFactor: 1.2,
            dofEnabled: false,
            bloomEnabled: false
        };
        return this.qualityConfig?.presets?.[level] ?? fallback;
    }

    applyQualityProfile(level) {
        const profile = this.getQualityProfile(level);
        const pixelRatioCap = profile.rendererPixelRatioMax ?? 1.25;
        this.pixelRatio = Math.min(window.devicePixelRatio, pixelRatioCap) * (profile.composerPixelRatio ?? 1);
        this.factor = profile.composerScaleFactor ?? 1;

        this.renderer.setPixelRatio(this.pixelRatio);
        if (this.composer) {
            this.composer.setPixelRatio(this.pixelRatio);
            this.composer.setSize(
                this.sizes.width / this.factor,
                this.sizes.height / this.factor
            );
        }

        this.applyMsaaSamples(profile.msaaSamples ?? 0);

        if (this.gradePass) {
            this.gradePass.uniforms.resolution.value.set(
                this.sizes.width * this.pixelRatio / this.factor,
                this.sizes.height * this.pixelRatio / this.factor
            );
        }

        if (this.bloomPass) {
            this.bloomPass.enabled = this.config.postprocessing.bloomPass?.enabled && !!profile.bloomEnabled;
        }

        if (this.dofPass) {
            this.dofPass.enabled = this.config.postprocessing.bokehPass?.enabled && !!profile.dofEnabled;
        }

        this.experience.world?.environment?.applyShadowQuality?.(level);
        this.experience.world?.trees?.applyQualityLevel?.(level);
    }

    shiftQuality(direction) {
        const currentIndex = this.qualityOrder.indexOf(this.qualityLevel);
        if (currentIndex === -1) return;

        const targetIndex = direction === 'down'
            ? Math.max(0, currentIndex - 1)
            : Math.min(this.qualityOrder.length - 1, currentIndex + 1);

        const nextLevel = this.qualityOrder[targetIndex];
        if (nextLevel === this.qualityLevel) return;

        this.qualityLevel = nextLevel;
        this.applyQualityProfile(this.qualityLevel);
    }

    updateAdaptiveQuality() {
        if (this.qualityConfig?.preset !== 'auto') return;

        const deltaMs = Math.max(1, this.time?.delta ?? 16);
        const fps = 1000 / deltaMs;

        this.fpsAccumulator += fps;
        this.fpsSamples += 1;

        if (this.fpsSamples * deltaMs < this.fpsWindowMs) return;

        const averageFps = this.fpsAccumulator / this.fpsSamples;
        if (averageFps < this.autoDownshiftFps) {
            this.shiftQuality('down');
        } else if (averageFps > this.autoUpshiftFps) {
            this.shiftQuality('up');
        }

        this.fpsAccumulator = 0;
        this.fpsSamples = 0;
    }

    smoothstep(edge0, edge1, x) {
        const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
        return t * t * (3 - 2 * t);
    }

    updateCinematicDOF() {
        if (!this.dofPass || !this.cinematicDOF) return;

        // Pas de profondeur de champ sur le plan orthographique
        if (this.experience.camera.controlMode === 'map') {
            this.dofPass.enabled = false;
            return;
        }

        if (!this.cinematicDOF.enabled) {
            this.dofPass.enabled = true;
            return;
        }

        const progress = this.experience.timeline?.progress;
        if (typeof progress !== 'number') {
            this.dofPass.enabled = false;
            return;
        }

        const feather = Math.max(0.001, this.cinematicDOF.feather);
        let bestWeight = 0;
        let bestSegment = null;

        for (const segment of this.cinematicDOF.segments) {
            const start = segment.start;
            const end = segment.end;
            if (typeof start !== 'number' || typeof end !== 'number' || end <= start) continue;

            const fadeIn = this.smoothstep(start, start + feather, progress);
            const fadeOut = 1.0 - this.smoothstep(end - feather, end, progress);
            const weight = Math.max(0, Math.min(1, fadeIn * fadeOut));

            if (weight > bestWeight) {
                bestWeight = weight;
                bestSegment = segment;
            }
        }

        this.dofPass.enabled = bestWeight > 0.001;
        if (!this.dofPass.enabled || !bestSegment) return;

        const focus = this.baseDOF.focus + (bestSegment.focus - this.baseDOF.focus) * bestWeight;
        const aperture = this.baseDOF.aperture + (bestSegment.aperture - this.baseDOF.aperture) * bestWeight;
        const maxblur = this.baseDOF.maxblur + (bestSegment.maxblur - this.baseDOF.maxblur) * bestWeight;

        this.dofPass.materialBokeh.uniforms.focus.value = focus;
        this.dofPass.materialBokeh.uniforms.aperture.value = aperture;
        this.dofPass.materialBokeh.uniforms.maxblur.value = maxblur;
    }

    resize() {
        this.applyQualityProfile(this.qualityLevel);
    }

    update() {
        this.updateAdaptiveQuality();
        this.updateCinematicDOF();
        if (this.renderPass) this.renderPass.camera = this.camera;
        if (this.gradePass) {
            const u = this.gradePass.uniforms;
            u.time.value = this.time.elapsed;
            u.exposure.value = this.renderer.toneMappingExposure;
        }
        this.composer.render();
    }

    destroy() {
        this.composer?.passes.forEach(pass => pass?.dispose?.());
        this.composer = null;

        if (this.debug?.active && this.debugFolder) {
            this.debugFolder.dispose();
            this.debugFolder = null;
        }
    }
}